/**
 * Snapshot domain des infos glider en temps réel — façade allégée sur
 * IIrisGliderState (qui exposait le shape IRIS brut + tout SEADEV/SEANAV/...).
 *
 * Sources de mise à jour, par ordre de fraîcheur croissante :
 *  - REST  MISSION_IRIS_GLIDER_STATE_REQUEST     → snapshot complet (mod_list + current)
 *  - REST  MISSION_IRIS_<MODULE>_REQUEST         → modules.{api_id} ciblé (per-module fetch)
 *  - WS    wobGliderNmea libelle=seadev          → devices.*
 *  - WS    wobGliderNmea libelle=seanav          → navTemplate (full)
 *  - WS    WobGliderModConf                      → modules.{api_id} après modif distante
 *  - WS    WobGliderAlarm                        → alarmCode (alarmNoAckFlags bloc 1 → bitmap)
 *  - WS    WobGliderNav / WobGliderStatus        → mode, navTemplate (selon source)
 *
 * Convention "last write wins" — les sources sont monotones-cohérentes (chaque
 * écriture représente la vérité à T, la plus récente gagne).
 */

// ---------------------------------------------------------------------------
// Modules IRIS — clés stables côté domain (= mod_api_id wire, sans préfixe)
// Inventaire validé sur payload réel (mod_list 2026-05-29).
// ---------------------------------------------------------------------------

export const GLIDER_MODULE_API_IDS = [
  'ExpectedNMEA', // ⚠ mod_key='expectednmea' (lowercase, sans préfixe module-)
  'AD2CP',
  'AIS',
  'Coriolis',
  'FTP', // ⚠ MAJUSCULES (pas 'Ftp')
  'FileTransfer',
  'IrisLogger',
  'MissionPlan',
  'NextSurfacingDo',
  'Onera',
  'QuickControl',
  'RSync', // ⚠ R+S majuscules (pas 'Rsync')
  'SendMail',
  'SingleWayPoint',
] as const

export type GliderModuleApiId = (typeof GLIDER_MODULE_API_IDS)[number]

/**
 * Mapping `mod_api_id` (domain) → clé `EnabledMOD` du payload IRIS (wire).
 * À utiliser pour extraire le flag enable depuis un IrisConfigContainer.
 *
 * Cas particulier `ExpectedNMEA` : `mod_key='expectednmea'` mais la convention
 * `EnabledMOD` n'est pas confirmée sur ce module (jamais consommée via fetch
 * direct). Conservé pour cohérence — à durcir si on en a besoin.
 */
export const MODULE_API_ID_TO_WIRE_KEY: Record<GliderModuleApiId, string> = {
  AD2CP: 'module-AD2CP',
  AIS: 'module-AIS',
  Coriolis: 'module-Coriolis',
  ExpectedNMEA: 'expectednmea',
  FTP: 'module-FTP',
  FileTransfer: 'module-FileTransfer',
  IrisLogger: 'module-IrisLogger',
  MissionPlan: 'module-MissionPlan',
  NextSurfacingDo: 'module-NextSurfacingDo',
  Onera: 'module-Onera',
  QuickControl: 'module-QuickControl',
  RSync: 'module-RSync',
  SendMail: 'module-SendMail',
  SingleWayPoint: 'module-SingleWayPoint',
}

// ---------------------------------------------------------------------------
// IGliderRuntime — snapshot domain (per-mission, comme l'ancien gliderState)
// ---------------------------------------------------------------------------

export interface IGliderDevices {
  flasher: boolean
  radio: boolean
  payload: boolean
  gps: boolean
  iridium: boolean
}

export interface IGliderNavTemplate {
  bd: number
  bu: number
  pd: number
  pu: number
  zb: number
  zt: number
  zs: number
  alt: number
  rate: number // ← SEANAV.data_rate ; consommé comme `sr` côté nav profile
}

export interface IGliderRuntime {
  /** Identifiant IRIS du glider — utile pour les handlers WS qui n'ont que gliId */
  gliId: number | null

  /** Cible courante du pilote (waypoint en cours) */
  targetLat: number | null
  targetLon: number | null
  targetPilot: string | null

  /** État glider runtime */
  mode: number | null // gli_mode IRIS
  alarmCode: number | null // gli_alr ; null ou 0 = pas d'alarme

  /** Périphériques on/off (issus SEADEV) */
  devices: IGliderDevices

  /** Profil nav courant (issu SEANAV ou WobGliderNav) — null si jamais vu */
  navTemplate: IGliderNavTemplate | null

  /** États enable des modules IRIS — clé = mod_api_id (cf GLIDER_MODULE_API_IDS) */
  modules: Partial<Record<GliderModuleApiId, boolean>>

  /** Dernier rafraîchissement (REST ou WS). Utile pour les écrans qui veulent dire "il y a 2 min". */
  lastUpdate: Date | null
}

export const getDefaultGliderRuntime = (gliId: number | null = null): IGliderRuntime => ({
  gliId,
  targetLat: null,
  targetLon: null,
  targetPilot: null,
  mode: null,
  alarmCode: null,
  devices: {
    flasher: false,
    radio: false,
    payload: false,
    gps: false,
    iridium: false,
  },
  navTemplate: null,
  modules: {},
  lastUpdate: null,
})
